import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  RotateCcw, 
  Save, 
  Lock,
  GitBranch,
  FileText,
  User,
  Calendar,
  AlertCircle,
  Loader,
  RefreshCw
} from 'lucide-react';
import versionHistoryService from '../../services/versionHistoryService';

const VersionHistory = forwardRef(({ surveyId, currentVersion, onRestoreVersion }, ref) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);

  useEffect(() => {
    console.log('🔍 VersionHistory mounted with surveyId:', surveyId);
    if (surveyId) {
      fetchVersionHistory();
    } else {
      setError('No survey ID provided - check assessmentConfig.survey_id in ScreenBuilder');
      setLoading(false);
    }
  }, [surveyId]);

  const fetchVersionHistory = async () => {
    if (!surveyId) {
      setError('Survey ID is missing');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      console.log('Fetching version history for survey:', surveyId);
      const data = await versionHistoryService.getVersionHistory(surveyId);
      console.log('Version history data:', data);
      setVersions(data.results || data || []);
    } catch (err) {
      setError(err.message || 'Failed to load version history');
      console.error('Version history error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreClick = (version) => {
    setConfirmRestore(version);
  };

  const handleRestoreConfirm = async () => {
    if (!confirmRestore) return;

    setRestoring(true);
    try {
      const restoredVersion = await versionHistoryService.restoreVersion(surveyId, confirmRestore.id);
      await fetchVersionHistory();
      if (onRestoreVersion) {
        onRestoreVersion(restoredVersion);
      }
      setConfirmRestore(null);
    } catch (err) {
      setError('Failed to restore version');
      console.error(err);
    } finally {
      setRestoring(false);
    }
  };

  const handleRestoreCancel = () => {
    setConfirmRestore(null);
  };

  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: fetchVersionHistory
  }));

  const getVersionTypeColor = (type) => {
    switch (type) {
      case 'auto':
        return 'bg-gray-100 text-gray-700 border-gray-300';
      case 'manual':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'publish':
        return 'bg-green-100 text-green-700 border-green-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getVersionTypeIcon = (type) => {
    switch (type) {
      case 'auto':
        return <Clock className="w-4 h-4" />;
      case 'manual':
        return <Save className="w-4 h-4" />;
      case 'publish':
        return <Lock className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-400">
        <Loader className="w-8 h-8 animate-spin mb-3" />
        <p className="text-sm">Loading versions...</p>
        {surveyId && <p className="text-xs mt-1">Survey ID: {surveyId}</p>}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Debug Info */}
      {!surveyId && (
        <div className="px-4 py-3 bg-red-900 bg-opacity-20 border-b border-red-700">
          <div className="flex items-start space-x-2 text-xs text-red-300">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Survey ID Missing</p>
              <p className="mt-1 opacity-80">assessmentConfig.survey_id is not set</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="px-4 py-3 bg-red-900 bg-opacity-20 border-b border-red-700">
          <div className="flex items-start space-x-2 text-xs text-red-300">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Error</p>
              <p className="mt-1 opacity-80">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Version Count */}
      <div className="px-4 py-2 bg-primary-50 border-b border-primary-100">
        <p className="text-xs text-primary-700 font-medium">
          {versions.length} {versions.length === 1 ? 'version' : 'versions'} available
        </p>
      </div>

      {/* Version List */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {versions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 px-4 py-8">
            <FileText className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm text-center">No version history yet</p>
            <p className="text-xs text-center mt-2 opacity-70">
              Versions will appear as you work
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {versions.map((version, index) => (
              <motion.div
                key={version.id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className={`border rounded p-3 transition-all ${
                  version.version_number === currentVersion
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-primary-300'
                }`}
              >
                {/* Version Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center ${
                      version.version_type === 'auto' ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 border border-gray-200 dark:border-gray-700' :
                      version.version_type === 'manual' ? 'bg-primary-100 text-primary-700 border border-primary-200' :
                      'bg-emerald-100 text-emerald-700 border border-emerald-200'
                    }`}>
                      {getVersionTypeIcon(version.version_type)}
                      <span className="ml-1">v{version.version_number}</span>
                    </span>
                    {version.is_locked && <Lock className="w-3 h-3 text-gray-500" />}
                  </div>
                  {version.version_number === currentVersion && (
                    <span className="px-2 py-0.5 bg-primary-600 text-white text-xs rounded">
                      Current
                    </span>
                  )}
                </div>

                {/* Change Summary */}
                {version.change_summary && (
                  <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                    {version.change_summary}
                  </p>
                )}

                {/* Metadata */}
                <div className="flex flex-col space-y-1 text-xs text-gray-500 mb-2">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(version.created)}</span>
                  </div>
                  {version.created_by_name && (
                    <div className="flex items-center space-x-1">
                      <User className="w-3 h-3" />
                      <span>{version.created_by_name}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-1">
                    <FileText className="w-3 h-3" />
                    <span>{version.screen_count} screens, {version.element_count} elements</span>
                  </div>
                </div>

                {/* Restore Button */}
                {version.version_number !== currentVersion && (
                  <button
                    onClick={() => handleRestoreClick(version)}
                    disabled={restoring}
                    className="w-full px-3 py-1.5 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1"
                  >
                    {restoring ? (
                      <Loader className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <RotateCcw className="w-3 h-3" />
                        <span>Restore</span>
                      </>
                    )}
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <p className="text-xs text-gray-500 text-center">
          Auto-saves every 5 minutes
        </p>
      </div>

      {/* Restore Confirmation Modal */}
      {confirmRestore && (
        <div className="fixed inset-0 bg-black bg-opacity-60 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg shadow-2xl dark:shadow-gray-900/20 max-w-md w-full mx-4 border border-gray-700">
            <div className="px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-400" />
                Confirm Version Restore
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-300 text-sm mb-4">
                Are you sure you want to restore <strong className="text-white">v{confirmRestore.version_number}</strong>?
              </p>
              <p className="text-gray-400 text-xs mb-2">
                This will create a new version based on the selected snapshot.
              </p>
              {confirmRestore.change_summary && (
                <div className="mt-3 p-3 bg-gray-900 rounded border border-gray-700">
                  <p className="text-xs text-gray-400 mb-1">Version Summary:</p>
                  <p className="text-sm text-gray-300">{confirmRestore.change_summary}</p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-900 bg-opacity-50 flex gap-3 justify-end rounded-b-lg">
              <button
                onClick={handleRestoreCancel}
                disabled={restoring}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRestoreConfirm}
                disabled={restoring}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {restoring ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4" />
                    Restore Version
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default VersionHistory;
